import SportIcon from '@/components/SportIcon';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Download, Filter, MoreHorizontal, Trash2, Plus, Upload, FileText, AlertCircle, ChevronDown, Loader2, RefreshCw, AlertTriangle, ChevronRight, Printer } from 'lucide-react';
import { downloadCSV, printPage } from '@/lib/exportUtils';
import Papa from 'papaparse';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const _isValidPhone = (v) => {
  if (!v) return true; // optional
  // Reject date patterns like 30/10/74 or 15-08-1990
  if (/^\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]/.test(v)) return false;
  // Count digits only — must have at least 10
  const digits = v.replace(/\D/g, '');
  return digits.length >= 10;
};

const playerSchema = z.object({
  first_name:    z.string().min(1, 'First name is required'),
  last_name:     z.string().optional().or(z.literal('')),
  gender:        z.enum(['male', 'female']),
  category:      z.string().default('advanced'),
  contact_phone: z.string()
    .refine(v => !v || _isValidPhone(v), 'Mobile number must be at least 10 digits (not a date)')
    .optional().or(z.literal('')),
  contact_email: z.string().email('Invalid email').optional().or(z.literal('')),
});

const PAGE_SIZE = 50;
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import api from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

// ── Player row sub-component ──────────────────────────────────
function PlayerRow({ player, playerCategories, teamAssignments, sports, getSportName, SPORT_ICONS, navigate, handleDeletePlayer, isSelected, onToggle }) {
  const name = player.full_name || player.name || `${player.first_name || ''} ${player.last_name || ''}`.trim() || 'Unknown';
  const cat  = playerCategories.find(c => c.code === (player.category || 'advanced'));
  const teamInfo = teamAssignments[player.id];
  const playerSports = player.sports || [];

  return (
    <TableRow className={`border-b border-[#252a37] hover:bg-[#1d2230]/30 transition-colors ${isSelected ? 'bg-[rgba(239,68,68,0.04)]' : ''}`}>
      {/* Checkbox */}
      <TableCell className="w-10 pl-4 pr-0">
        <input type="checkbox" checked={isSelected} onChange={() => onToggle(player.id)}
          className="h-4 w-4 rounded border-[#252a37] bg-[#1d2230] accent-[#ef4444] cursor-pointer"
          onClick={e => e.stopPropagation()}/>
      </TableCell>
      {/* Member column: name + sports badges */}
      <TableCell>
        <div className="flex items-center gap-3 cursor-pointer group"
          onClick={() => navigate(`/admin/players/${player.id}`, { state: { player } })}>
          <div className="h-9 w-9 rounded-full bg-[#1d2230] flex items-center justify-center font-medium flex-shrink-0 overflow-hidden"
            style={{ color: cat?.color || '#f5c518' }}>
            {player.photo_url
              ? <img src={player.photo_url} className="h-full w-full object-cover" alt={name}/>
              : name[0] || 'P'
            }
          </div>
          <div className="min-w-0">
            <span className="font-medium text-[#f0f2f7] block group-hover:text-[#f5c518] transition-colors truncate">{name}</span>
            {/* Sports badges inline */}
            {playerSports.length > 0 ? (
              <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                {playerSports.map(sc => (
                  <span key={sc} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-[#1d2230] text-[#8a93a3] border border-[#252a37] whitespace-nowrap">
                    <SportIcon sport={sc} size={14}/>
                    <span>{getSportName(sc)}</span>
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-[10px] text-[#8a93a3]">No sports assigned</span>
            )}
          </div>
        </div>
      </TableCell>

      {/* Category */}
      <TableCell>
        {cat ? (
          <Badge className="border text-xs font-semibold"
            style={{ backgroundColor: `${cat.color}20`, color: cat.color, borderColor: `${cat.color}50` }}>
            {cat.is_platinum && '★ '}{cat.name}
          </Badge>
        ) : (
          <Badge className="bg-[#1d2230] text-[#8a93a3] border border-[#252a37] text-xs">Advanced</Badge>
        )}
      </TableCell>

      {/* Team */}
      <TableCell>
        {teamInfo ? (
          <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-semibold"
            style={{ backgroundColor: `${teamInfo.team_color}20`, color: teamInfo.team_color, border: `1px solid ${teamInfo.team_color}40` }}>
            {teamInfo.team_name}
          </span>
        ) : (
          <span className="text-xs text-[#8a93a3]">—</span>
        )}
      </TableCell>

      {/* Contact */}
      <TableCell>
        <div className="text-sm">
          {player.contact_phone && (
            <span className="text-[#f0f2f7] block font-mono text-xs">{player.contact_phone}</span>
          )}
          {player.contact_email && (
            <span className="text-[#8a93a3] block text-xs truncate max-w-[150px]">{player.contact_email}</span>
          )}
          {!player.contact_phone && !player.contact_email && <span className="text-[#8a93a3]">-</span>}
        </div>
      </TableCell>

      {/* Status */}
      <TableCell>
        <Badge className={`${player.status === 'active'
          ? 'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border-[rgba(34,197,94,0.35)]'
          : 'bg-[rgba(239,68,68,0.15)] text-[#ef4444] border-[rgba(239,68,68,0.35)]'} border`}>
          {player.status || 'active'}
        </Badge>
      </TableCell>

      {/* Actions */}
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8a93a3] hover:text-[#f0f2f7]">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#161a26] border-[#252a37]">
            <DropdownMenuItem
              onClick={() => handleDeletePlayer(player.id)}
              className="text-[#ef4444] focus:bg-[#1d2230] cursor-pointer">
              <Trash2 className="h-4 w-4 mr-2" />Remove Member
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

export default function PlayersPage() {
  const { activeOrgSlug, user, isAdmin } = useAuthStore();
  const admin = isAdmin();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [duplicatesFound, setDuplicatesFound] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sportFilter, setSportFilter] = useState('all');
  const [sports, setSports] = useState([]);
  const [playerCategories, setPlayerCategories] = useState([]);
  const [teamAssignments, setTeamAssignments] = useState({});
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [missingReport, setMissingReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [missingFilter, setMissingFilter] = useState(null); // 'dob'|'email'|'sports'|'photo'|'phone'|null
  const [syncing, setSyncing] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const [resettingPwd, setResettingPwd] = useState(false);
  // Bulk member selection + delete
  const [selectedMemberIds, setSelectedMemberIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting]           = useState(false);
  // DOB bulk update
  const [dobOpen, setDobOpen]           = useState(false);
  const [dobFile, setDobFile]           = useState(null);
  const [dobPreview, setDobPreview]     = useState([]);
  const [dobUploading, setDobUploading] = useState(false);
  const debounceRef = useRef(null);

  const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
    resolver: zodResolver(playerSchema),
    defaultValues: { first_name: '', last_name: '', gender: 'male', category: 'advanced', contact_phone: '', contact_email: '' },
  });

  // Server-side search with debounce — reset to page 1 on search change
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchPlayers(search, PAGE_SIZE);  // was incorrectly passing 1 as limit
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search, activeOrgSlug]);

  // Load sports + categories + team assignments once; missing-fields report only for admins
  useEffect(() => { fetchSports(); fetchPlayerCategories(); fetchTeamAssignments(); if (admin) fetchMissingReport(); }, [orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPlayerCategories = async () => {
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/player-categories`,
        { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setPlayerCategories(Array.isArray(data) ? data : []);
    } catch (_) {}
  };

  const fetchTeamAssignments = async () => {
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/player-team-assignments`,
        { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setTeamAssignments(await res.json());
    } catch (_) {}
  };

  const fetchMissingReport = async () => {
    try {
      setReportLoading(true);
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/players/missing-fields`,
        { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setMissingReport(await res.json());
    } catch (_) {}
    finally { setReportLoading(false); }
  };

  const handleSyncToUsers = async () => {
    if (!confirm('This will create login accounts for all members who have a phone number but no account yet. Continue?')) return;
    setSyncing(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/sync-members-to-users`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      toast.success(`${data.synced} member account${data.synced !== 1 ? 's' : ''} created · ${data.skipped_no_phone} skipped (no phone)`);
    } catch (_) { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  const handleDedup = async () => {
    if (!confirm('This will find and remove duplicate members (same phone or same name), keeping the record with the most information. Continue?')) return;    setDeduping(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/players/deduplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.deleted === 0) {
        toast.success('No duplicates found — your member list is clean!');
      } else {
        toast.success(`${data.deleted} duplicate${data.deleted > 1 ? 's' : ''} removed · ${data.remaining} members remain`);
      }
      fetchPlayers(search, PAGE_SIZE);
      fetchMissingReport();
    } catch (_) { toast.error('Deduplication failed'); }
    finally { setDeduping(false); }
  };

  const handleNormalizePhones = async () => {
    setNormalizing(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/players/normalize-phones`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.players_updated === 0 && data.users_updated === 0) {
        toast.success('All phones already have +91 prefix — nothing to update');
      } else {
        toast.success(`${data.players_updated} member phone${data.players_updated !== 1 ? 's' : ''} normalized · ${data.users_updated} user account${data.users_updated !== 1 ? 's' : ''} updated`);
        fetchPlayers(search, PAGE_SIZE);
      }
    } catch (_) { toast.error('Phone normalization failed'); }
    finally { setNormalizing(false); }
  };

  const handleResetPasswords = async () => {
    if (!confirm('This will create login accounts for ALL members AND set password to Circles26 for everyone. Continue?')) return;
    setResettingPwd(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/reset-member-passwords`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      toast.success(`Done! ${data.created} new accounts · ${data.reset} passwords reset · Default: ${data.default_password}`);
    } catch (_) { toast.error('Failed to reset passwords'); }
    finally { setResettingPwd(false); }
  };

  const toggleMember = (id) =>
    setSelectedMemberIds(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });

  const toggleAllMembers = () =>
    setSelectedMemberIds(selectedMemberIds.size === filteredPlayers.length && filteredPlayers.length > 0
      ? new Set()
      : new Set(filteredPlayers.map(p => p.id)));

  const handleBulkDeleteMembers = async () => {
    const count = selectedMemberIds.size;
    if (!count) return;
    if (!confirm(`Permanently delete ${count} member${count>1?'s':''}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      await Promise.all([...selectedMemberIds].map(id =>
        fetch(`/api/orgs/${orgSlug}/players/${id}`, {
          method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
        })
      ));
      toast.success(`${count} member${count>1?'s':''} deleted`);
      setSelectedMemberIds(new Set());
      fetchPlayers(search, PAGE_SIZE, false);
      fetchMissingReport();
    } catch { toast.error('Some deletions failed'); }
    finally { setBulkDeleting(false); }
  };
  const normDob = (raw) => {
    if (!raw) return '';
    raw = raw.trim();
    const m = raw.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})$/);
    if (m) {
      let [, d, mo, y] = m;
      if (y.length === 2) y = parseInt(y) > 30 ? '19' + y : '20' + y;
      return `${y}-${mo.padStart(2,'0')}-${d.padStart(2,'0')}`;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
    return '';
  };

  const parseDobRow = (cols) => {
    // Auto-detect format:
    // 2-col: [Full Name, DOB]
    // 3-col: [First Name, Last Name, DOB]  ← actual CSV format
    const c0 = (cols[0] || '').trim();
    const c1 = (cols[1] || '').trim();
    const c2 = (cols[2] || '').trim();
    if (!c0) return null; // skip empty rows
    // Try 3-column format first (more specific)
    const dob3 = normDob(c2);
    if (dob3 && c1 && !/date|birth|dob/i.test(c2)) {
      return { name: `${c0} ${c1}`.trim(), dob: dob3, raw_dob: c2 };
    }
    // Try 2-column format
    const dob2 = normDob(c1);
    if (dob2 && !/name|last/i.test(c1)) {
      return { name: c0, dob: dob2, raw_dob: c1 };
    }
    return null; // header row or unrecognized
  };

  const handleDobFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setDobFile(file);
    Papa.parse(file, {
      header: false, skipEmptyLines: 'greedy',
      complete: (r) => {
        const rows = r.data.map(parseDobRow).filter(Boolean);
        setDobPreview(rows.slice(0, 6).map(r => ({ ...r, raw_dob: r.raw_dob })));
      }
    });
  };

  const handleDobUpload = async () => {
    if (!dobFile) { toast.error('Select a CSV file'); return; }
    setDobUploading(true);
    try {
      const token = localStorage.getItem('btccp_access_token');
      // Parse synchronously then upload
      const text = await dobFile.text();
      const parsed = Papa.parse(text, { header: false, skipEmptyLines: 'greedy' });
      const updates = parsed.data
        .map(parseDobRow)
        .filter(u => u !== null && u.dob);

      if (updates.length === 0) {
        toast.error('No valid rows found. CSV needs: Name, Date of Birth');
        return;
      }

      const res = await fetch(`/api/orgs/${orgSlug}/players/bulk-update-dob`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const data = await res.json();
      if (res.ok) {
        const parts = [`${data.updated} updated`];
        if (data.not_found?.length) parts.push(`${data.not_found.length} not found`);
        if (data.skipped_no_dob) parts.push(`${data.skipped_no_dob} skipped (no DOB)`);
        toast.success(parts.join(' · '));
        if (data.not_found?.length)
          toast.warning(`Not matched: ${data.not_found.slice(0,3).join(', ')}${data.not_found.length > 3 ? '...' : ''}`);
        setDobOpen(false); setDobFile(null); setDobPreview([]);
        fetchPlayers(search, PAGE_SIZE);
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch (err) { toast.error(`Error: ${err.message}`); }
    finally { setDobUploading(false); }
  };

  const fetchPlayers = async (q = '', limit = PAGE_SIZE) => {
    try {
      if (q) setSearchLoading(true);
      else setLoading(true);

      const params = { limit };
      if (q.trim()) params.q = q.trim();
      const response = await api.get(`/orgs/${orgSlug}/players`, { params });
      const data = Array.isArray(response.data) ? response.data : response.data.items || [];
      setPlayers(data);
      setHasMore(data.length === limit);
      // Update total count on initial load
      if (!q) {
        api.get(`/orgs/${orgSlug}/players`, { params: { limit: 9999 } }).then(r => {
          const all = Array.isArray(r.data) ? r.data : r.data.items || [];
          setTotalCount(all.length);
        }).catch(() => setTotalCount(data.length));
      }
    } catch {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchSports = async () => {
    try {
      const response = await api.get(`/orgs/${orgSlug}/sports`);
      setSports(Array.isArray(response.data) ? response.data : []);
    } catch (_) {}
  };

  const handleDeletePlayer = async (playerId) => {
    if (!confirm('Are you sure you want to delete this player?')) return;
    try {
      await api.delete(`/orgs/${orgSlug}/players/${playerId}`);
      toast.success('Member removed successfully');
      fetchPlayers(search, PAGE_SIZE);
    } catch {
      toast.error('Failed to delete player');
    }
  };

  const handleCreatePlayer = async (data) => {
    setCreating(true);
    // Normalize phone before processing
    const normPhone = _normPhone(data.contact_phone || '');
    try {
      // Check for duplicate phone before creating
      if (normPhone) {
        const existing = players.find(p => (p.contact_phone||'').replace(/\s/g,'') === normPhone.replace(/\s/g,''));
        if (existing) {
          toast.error(`Phone ${normPhone} already exists — ${existing.full_name || existing.first_name}`);
          setCreating(false);
          return;
        }
      }
      await api.post(`/orgs/${orgSlug}/players`, {
        first_name:    data.first_name,
        last_name:     data.last_name || undefined,
        gender:        data.gender,
        category:      data.category || 'advanced',
        contact_phone: normPhone || undefined,
        contact_email: data.contact_email || undefined,
      });
      toast.success('Member added successfully');
      setCreateOpen(false);
      reset();
      fetchPlayers(search, PAGE_SIZE);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setCreating(false);
    }
  };


  // ── CSV parser — handles both headerless (positional) and headered files ──
  const _normPhone = (p) => {
    if (!p) return '';
    let d = String(p).replace(/[\s\-().]/g, '').trim();
    if (!d) return '';
    // Already has + prefix — keep as-is
    if (d.startsWith('+')) return d;
    // 12 digits starting with 91 (e.g., 919876543210) → add +
    if (/^91\d{10}$/.test(d)) return '+' + d;
    // 10-digit Indian number → add +91
    if (/^\d{10}$/.test(d)) return '+91' + d;
    return d;
  };

  const _detectAndParse = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      let text = e.target.result;
      // Strip BOM
      if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
      const firstLine = text.split('\n')[0] || '';
      const hasHeaders = /name|mobile|phone|email|first/i.test(firstLine);

      // Parse DOB: normalize formats like 30/10/74 → 1974-10-30
      const normDob = (raw) => {
        if (!raw) return '';
        raw = raw.trim();
        // DD/MM/YY or DD/MM/YYYY
        const m = raw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
        if (m) {
          let [, d, mo, y] = m;
          if (y.length === 2) y = parseInt(y) > 30 ? '19' + y : '20' + y;
          return `${y}-${mo.padStart(2,'0')}-${d.padStart(2,'0')}`;
        }
        // Already ISO
        if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0,10);
        return '';
      };

      if (hasHeaders) {
        Papa.parse(text, {
          header: true, skipEmptyLines: 'greedy',
          transformHeader: h => h.trim().toLowerCase().replace(/\s+/g,'_').replace(/[^\w]/g,''),
          complete: (r) => {
            const rows = r.data.map(row => {
              const lc = Object.fromEntries(Object.entries(row).map(([k,v]) => [k, String(v||'').trim()]));
              const rawPhone = lc.mobile_1||lc.mobile1||lc.phone||lc.mobile||lc.contact_phone||lc.mobileno||'';
              return {
                first_name: (lc.first_name||lc.firstname||lc.fname||(lc.name?lc.name.split(' ')[0]:'')).trim(),
                last_name: (lc.last_name||lc.lastname||(lc.name?lc.name.split(' ').slice(1).join(' '):'')).trim(),
                contact_phone: _normPhone(rawPhone),
                contact_email: lc.email||lc.contact_email||'',
                dob: normDob(lc.dob||lc.date_of_birth||lc.dateofbirth||lc.birthdate||''),
              };
            });
            resolve({ rows, detected: 'headered', fields: r.meta?.fields || [] });
          }
        });
      } else {
        // NO headers — positional: col0=FirstName, col1=LastName, col2=Mobile1, col3=Mobile2, col4=Email, col5=DOB
        const rows = text.split('\n').filter(l => l.trim()).map(line => {
          const cols = line.split(',').map(c => c.trim());
          return {
            first_name: cols[0] || '',
            last_name:  cols[1] || '',
            contact_phone: _normPhone(cols[2]),
            contact_phone2: _normPhone(cols[3]),
            contact_email: cols[4] || '',
            dob: normDob(cols[5] || ''),
          };
        });
        resolve({ rows, detected: 'no-headers', fields: ['col0(Name)','col1(LastName)','col2(Mobile1)','col3(Mobile2)','col4(Email)','col5(DOB)'] });
      }
    };
    reader.readAsText(file, 'UTF-8');
  });

  const handleBulkFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error('Excel files not supported. Please open in Excel → Save As → CSV, then upload the .csv file.');
      e.target.value = '';
      return;
    }
    setBulkFile(file);
    // Preview using positional parse
    _detectAndParse(file).then(({ rows }) => setBulkPreview(rows.slice(0, 5))).catch(() => {});
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) { toast.error('Please select a CSV file'); return; }
    setBulkUploading(true);
    try {
      const { rows, detected, fields } = await _detectAndParse(bulkFile);

      // Use primary phone; fall back to secondary if primary is empty
      const allRows = rows.map(r => ({
        ...r,
        contact_phone: r.contact_phone || r.contact_phone2 || '',
      }));

      const players = allRows.filter(p => p.first_name && p.contact_phone && p.contact_phone.length >= 7);
      const skipped  = allRows.length - players.length;

      if (players.length === 0) {
        toast.error(`No valid rows found. Detected format: ${detected}. Columns: [${fields.slice(0,6).join(', ')}]. Need name + mobile.`);
        setBulkUploading(false);
        return;
      }

      // Duplicate detection: check against existing member phones
      const token = localStorage.getItem('btccp_access_token');
      let existingPhones = new Set();
      try {
        const existRes = await fetch(`/api/orgs/${orgSlug}/players?limit=9999`,
          { headers: { 'Authorization': `Bearer ${token}` } });
        if (existRes.ok) {
          const existData = await existRes.json();
          const existList = Array.isArray(existData) ? existData : existData.items || [];
          existingPhones = new Set(existList.map(p => (p.contact_phone||'').replace(/\s/g,'')));
        }
      } catch (_) {}

      const duplicates = players.filter(p => existingPhones.has((p.contact_phone||'').replace(/\s/g,'')));
      const unique     = players.filter(p => !existingPhones.has((p.contact_phone||'').replace(/\s/g,'')));
      const skipDups   = skipped + duplicates.length;

      if (duplicates.length > 0) {
        toast.info(`${duplicates.length} duplicate phone${duplicates.length>1?'s':''} detected and skipped: ${duplicates.slice(0,3).map(d=>d.contact_phone).join(', ')}${duplicates.length>3?' ...':''}`);
      }
      if (unique.length === 0) {
        toast.error(`All ${players.length} rows are duplicates — already in your member list.`);
        setBulkUploading(false);
        return;
      }

      const res = await fetch(`/api/orgs/${orgSlug}/players/bulk`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ players: unique.map(p => ({
          first_name:    p.first_name,
          last_name:     p.last_name || '',
          contact_phone: p.contact_phone,
          contact_email: p.contact_email || '',
          dob:           p.dob || undefined,
        })) }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || `Upload failed (${res.status})`);
      } else {
        const parts = [];
        parts.push(`${data.created || unique.length} members added`);
        if (duplicates.length > 0) parts.push(`${duplicates.length} duplicates skipped`);
        if (skipped > 0) parts.push(`${skipped} rows missing name/phone`);
        toast.success(parts.join(' · '));
        setDuplicatesFound(duplicates);
        setBulkOpen(false);
        setBulkFile(null);
        setBulkPreview([]);
        fetchPlayers(search, PAGE_SIZE);
      }
    } catch (err) {
      toast.error(`Upload error: ${err.message}`);
    } finally {
      setBulkUploading(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      toast.info('Fetching all members for export...');
      const response = await api.get(`/orgs/${orgSlug}/players`, { params: { limit: 9999 } });
      const allMembers = Array.isArray(response.data) ? response.data : response.data.items || [];
      const calcAge = (dob) => {
        if (!dob) return '';
        try { return Math.floor((Date.now() - new Date(dob)) / (365.25*24*60*60*1000)); } catch { return ''; }
      };
      const headers = ['Full Name','First Name','Last Name','Phone','Email','Date of Birth','Age','Category','Gender','Sports','Status'];
      const rows = allMembers.map(p => [
        p.full_name || '',
        p.first_name || '',
        p.last_name  || '',
        p.contact_phone || '',
        p.contact_email || '',
        p.dob || '',
        calcAge(p.dob),
        p.category || 'advanced',
        p.gender || '',
        (p.sports||[]).join(', '),
        p.status || 'active',
      ]);
      downloadCSV(`BTCCP_Members`, headers, rows);
      toast.success(`${allMembers.length} members exported`);
    } catch (_) {
      toast.error('Export failed');
    }
  };

  // Download CSV template for members
  const handleDownloadTemplate = () => {
    const csv = [
      'Name,Last Name,Mobile 1,Mobile 2,Email,Date of Birth',
      'Rahul,Sharma,+919876543210,+919876543211,rahul@example.com,15/03/1990',
      'Priya,Patel,+919876543212,,priya@example.com,22/07/1995',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'BTCCP_Members_Template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded — fill in your member details and upload');
  };

  // Search is now server-side via ?q= — only sport filter + missing filter client-side
  const filteredByMissing = (() => {
    if (!missingFilter) return players;
    if (missingFilter === 'no_team') return players.filter(p => !teamAssignments[p.id]);
    return players.filter(p => missingReport?.details[`missing_${missingFilter}`]?.some(m => m.id === p.id));
  })();

  const filteredPlayers = (() => {
    let list = filteredByMissing;
    if (sportFilter === 'non_playing') {
      // Filter members whose category is non_playing
      list = list.filter(p => p.category === 'non_playing');
    } else if (sportFilter !== 'all') {
      list = list.filter(player => (player.sports || []).includes(sportFilter));
    }
    return list;
  })();

  // Sport name lookup helper
  const getSportName = (code) =>
    sports.find(s => s.code === code)?.name || code.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());

  const SPORT_ICONS = {
    table_tennis:'🏓', badminton:'🏸', chess:'♟️',
    carrom:'🎯', pickleball:'🥒', squash:'🎾',
    cricket:'🏏', football:'⚽',
  };

  // Group into Playing (in a team) and Unassigned
  const playingPlayers    = filteredPlayers.filter(p => teamAssignments[p.id]);
  const unassignedPlayers = filteredPlayers.filter(p => !teamAssignments[p.id]);

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-players-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7] mb-1">Members</h1>
          <p className="text-[#8a93a3]">
            Organisation member database —{' '}
            <span className="text-[#f5c518] font-semibold">
              {totalCount > 0 ? `${totalCount} total members` : `${players.length}${hasMore ? '+' : ''} members`}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {admin && (
            <>
              <Button onClick={handleExportCsv} variant="outline"
                className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]"
                data-testid="players-export-csv-button">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button onClick={printPage} variant="outline"
                className="border-[#252a37] text-[#8a93a3] hover:bg-[#161a26] hover:text-[#f0f2f7]">
                <Printer className="h-4 w-4 mr-2" />
                Print / PDF
              </Button>
              <Button onClick={handleDownloadTemplate} variant="outline"
                className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]"
                data-testid="members-download-template-button">
                <Download className="h-4 w-4 mr-2" />
                Get Template
              </Button>
              <Button onClick={() => setDobOpen(true)} variant="outline"
                className="border-[rgba(245,197,24,0.4)] text-[#f5c518] hover:bg-[rgba(245,197,24,0.08)]"
                data-testid="players-update-dob-button"
                title="Upload a CSV with Name + DOB to update existing members">
                <Upload className="h-4 w-4 mr-2" />
                Update DOB
              </Button>
              <Button onClick={handleSyncToUsers} variant="outline" disabled={syncing}
                className="border-[rgba(34,197,94,0.4)] text-[#22c55e] hover:bg-[rgba(34,197,94,0.08)]"
                data-testid="sync-members-to-users-button"
                title="Create login accounts for all members with a phone number">
                {syncing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Syncing...</> : <><RefreshCw className="h-4 w-4 mr-2"/>Sync to Users</>}
              </Button>
              <Button onClick={handleDedup} variant="outline" disabled={deduping}
                className="border-[rgba(239,68,68,0.4)] text-[#ef4444] hover:bg-[rgba(239,68,68,0.08)]"
                data-testid="remove-duplicates-button"
                title="Remove duplicate members (same phone or same name)">
                {deduping ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Removing...</> : <><Trash2 className="h-4 w-4 mr-2"/>Remove Duplicates</>}
              </Button>
              <Button onClick={handleNormalizePhones} variant="outline" disabled={normalizing}
                className="border-[rgba(59,130,246,0.4)] text-[#3b82f6] hover:bg-[rgba(59,130,246,0.08)]"
                data-testid="normalize-phones-button"
                title="Add +91 prefix to all 10-digit phone numbers">
                {normalizing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Fixing...</> : <>+91 Fix Phones</>}
              </Button>
              <Button onClick={handleResetPasswords} variant="outline" disabled={resettingPwd}
                className="border-[rgba(34,197,94,0.4)] text-[#22c55e] hover:bg-[rgba(34,197,94,0.08)]"
                data-testid="reset-passwords-button"
                title="Create accounts for all members and set password to Circles26">
                {resettingPwd ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Resetting...</> : <>Reset All Passwords</>}
              </Button>
              <Button onClick={() => setBulkOpen(true)} variant="outline"
                className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]"
                data-testid="players-bulk-upload-button">
                <Upload className="h-4 w-4 mr-2" />
                Upload CSV
              </Button>
              <Button onClick={() => setCreateOpen(true)} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
                <Plus className="h-4 w-4 mr-2" />
                Add Member
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          {searchLoading
            ? <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#f5c518] animate-spin" />
            : <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]" />
          }
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members by name… (server-side)"
            className="pl-10 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]"
            data-testid="players-search-input"
          />
        </div>
        <Select value={sportFilter} onValueChange={setSportFilter}>
          <SelectTrigger className="w-[180px] bg-[#161a26] border-[#252a37] text-[#f0f2f7]" data-testid="players-filters-button">
            <Filter className="h-4 w-4 mr-2 text-[#8a93a3]" />
            <SelectValue placeholder="Filter by sport" />
          </SelectTrigger>
          <SelectContent className="bg-[#161a26] border-[#252a37]">
            <SelectItem value="all" className="text-[#f0f2f7] focus:bg-[#1d2230]">All Members</SelectItem>
            <SelectItem value="non_playing" className="text-[#8a93a3] focus:bg-[#1d2230]">⛔ Non-Playing</SelectItem>
            {sports.map((sport) => (
              <SelectItem 
                key={sport.id || sport.code} 
                value={sport.code}
                className="text-[#f0f2f7] focus:bg-[#1d2230]"
              >
                {sport.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Data Health / Missing Fields Report */}
      {missingReport && missingReport.total > 0 && (
        <Card className="bg-[#161a26] border-[#252a37] mb-4" data-testid="missing-fields-report">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-[#f5c518]"/>
                <span className="text-sm font-semibold text-[#f0f2f7]">Data Health Report</span>
                <span className="text-xs text-[#8a93a3]">— click to filter · click ↓ to download list</span>
              </div>
              {missingFilter && (
                <button onClick={() => setMissingFilter(null)}
                  className="text-xs text-[#8a93a3] hover:text-[#f0f2f7] flex items-center gap-1">
                  Clear filter ×
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'dob',    label: 'Missing DOB',    color: '#f97316' },
                { key: 'email',  label: 'Missing Email',  color: '#3b82f6' },
                { key: 'sports', label: 'No Sports',      color: '#8b5cf6' },
                { key: 'photo',  label: 'No Photo',       color: '#14b8a6' },
                { key: 'phone',  label: 'No Phone',       color: '#ef4444' },
              ].map(({ key, label, color }) => {
                const count = missingReport.summary[`missing_${key}`] || 0;
                if (count === 0) return null;
                const isActive = missingFilter === key;
                const handleDownload = (e) => {
                  e.stopPropagation();
                  const members = missingReport.details[`missing_${key}`] || [];
                  const header = 'Name,ID';
                  const rows = members.map(m => `"${(m.name||'').replace(/"/g,'""')}",${m.id}`);
                  const csv = '\uFEFF' + [header, ...rows].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${label.replace(/ /g,'_')}_${new Date().toISOString().slice(0,10)}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                };
                return (
                  <div key={key} className="flex items-center gap-0.5">
                    <button
                      onClick={() => setMissingFilter(isActive ? null : key)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-l-lg text-xs font-semibold transition-all border-y border-l"
                      style={{
                        backgroundColor: isActive ? `${color}20` : `${color}10`,
                        color: color,
                        borderColor: isActive ? `${color}60` : `${color}30`,
                      }}
                      data-testid={`missing-filter-${key}`}
                    >
                      <span className="text-base leading-none">
                        {key === 'dob' ? '📅' : key === 'email' ? '✉️' : key === 'sports' ? '🏅' : key === 'photo' ? '📷' : '📵'}
                      </span>
                      {label}: <strong>{count}</strong>
                      {isActive && <span className="ml-1">✓</span>}
                    </button>
                    <button
                      onClick={handleDownload}
                      className="flex items-center justify-center px-2 py-1.5 rounded-r-lg text-xs transition-all border-y border-r hover:opacity-80"
                      style={{
                        backgroundColor: isActive ? `${color}20` : `${color}10`,
                        color: color,
                        borderColor: isActive ? `${color}60` : `${color}30`,
                      }}
                      title={`Download ${label} list as CSV`}
                      data-testid={`missing-download-${key}`}
                    >
                      <Download className="h-3 w-3"/>
                    </button>
                  </div>
                );
              })}
            </div>
            {missingFilter && (
              <p className="text-xs text-[#8a93a3] mt-2">
                Showing {filteredPlayers.length} members missing {missingFilter === 'dob' ? 'date of birth' : missingFilter === 'sports' ? 'sport assignment' : missingFilter}
                <button onClick={() => {
                  const members = missingReport.details[`missing_${missingFilter}`] || [];
                  const header = 'Name,ID';
                  const rows = members.map(m => `"${(m.name||'').replace(/"/g,'""')}",${m.id}`);
                  const csv = '\uFEFF' + [header, ...rows].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `missing_${missingFilter}_${new Date().toISOString().slice(0,10)}.csv`; a.click();
                  URL.revokeObjectURL(url);
                }} className="ml-2 text-[#f5c518] hover:underline inline-flex items-center gap-1">
                  <Download className="h-3 w-3"/> Download this list
                </button>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Players Table */}
      <Card className="bg-[#161a26] border-[#252a37]">
        <ScrollArea className="w-full">
          {loading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-full bg-[#1d2230]" />
                  <Skeleton className="h-4 w-32 bg-[#1d2230]" />
                  <Skeleton className="h-4 w-24 bg-[#1d2230]" />
                  <Skeleton className="h-4 w-20 bg-[#1d2230]" />
                </div>
              ))}
            </div>
          ) : filteredPlayers.length > 0 ? (
            <Table data-testid="players-table">
              <TableHeader>
                <TableRow className="border-b border-[#252a37] bg-[#1d2230]/50">
                  <TableHead className="w-10 pl-4 pr-0">
                    <input type="checkbox"
                      checked={selectedMemberIds.size === filteredPlayers.length && filteredPlayers.length > 0}
                      ref={el => { if (el) el.indeterminate = selectedMemberIds.size > 0 && selectedMemberIds.size < filteredPlayers.length; }}
                      onChange={toggleAllMembers}
                      className="h-4 w-4 rounded border-[#252a37] bg-[#1d2230] accent-[#ef4444] cursor-pointer"/>
                  </TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Member</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Category</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Team</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Contact</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Status</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Bulk delete bar */}
                {selectedMemberIds.size > 0 && (
                  <TableRow className="border-b border-[#252a37] bg-[rgba(239,68,68,0.06)]">
                    <TableCell colSpan={7} className="py-2 px-4">
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-[#f0f2f7] font-medium">{selectedMemberIds.size} selected</span>
                        <button onClick={handleBulkDeleteMembers} disabled={bulkDeleting}
                          className="flex items-center gap-1.5 text-xs text-[#ef4444] hover:text-[#f87171] border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                          data-testid="bulk-delete-members-btn">
                          <Trash2 className="h-3.5 w-3.5"/>
                          {bulkDeleting ? 'Deleting...' : `Delete ${selectedMemberIds.size} Member${selectedMemberIds.size>1?'s':''}`}
                        </button>
                        <button onClick={() => setSelectedMemberIds(new Set())} className="text-xs text-[#8a93a3] hover:text-[#f0f2f7]">
                          Clear selection
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {/* Playing members — in a team */}
                {playingPlayers.length > 0 && (
                  <TableRow className="border-b border-[#252a37] bg-[rgba(245,197,24,0.04)]">
                    <TableCell colSpan={7} className="py-2 px-4">
                      <span className="text-xs font-semibold text-[#f5c518] uppercase tracking-wide">
                        Players ({playingPlayers.length})
                      </span>
                    </TableCell>
                  </TableRow>
                )}
                {playingPlayers.map((player) => (
                  <PlayerRow key={player.id} player={player}
                    playerCategories={playerCategories}
                    teamAssignments={teamAssignments}
                    sports={sports}
                    getSportName={getSportName}
                    SPORT_ICONS={SPORT_ICONS}
                    navigate={navigate}
                    handleDeletePlayer={handleDeletePlayer}
                    isSelected={selectedMemberIds.has(player.id)}
                    onToggle={toggleMember}
                  />
                ))}
                {/* Unassigned members */}
                {unassignedPlayers.length > 0 && (
                  <TableRow className="border-b border-[#252a37] bg-[rgba(138,147,163,0.04)]">
                    <TableCell colSpan={7} className="py-2 px-4">
                      <span className="text-xs font-semibold text-[#8a93a3] uppercase tracking-wide">
                        Not Yet in a Team ({unassignedPlayers.length})
                      </span>
                    </TableCell>
                  </TableRow>
                )}
                {unassignedPlayers.map((player) => (
                  <PlayerRow key={player.id} player={player}
                    playerCategories={playerCategories}
                    teamAssignments={teamAssignments}
                    sports={sports}
                    getSportName={getSportName}
                    SPORT_ICONS={SPORT_ICONS}
                    navigate={navigate}
                    handleDeletePlayer={handleDeletePlayer}
                    isSelected={selectedMemberIds.has(player.id)}
                    onToggle={toggleMember}
                  />
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center">
              <Users className="h-12 w-12 text-[#8a93a3] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#f0f2f7] mb-2">
                {search || sportFilter !== 'all' ? 'No players found' : 'No players yet'}
              </h3>
              <p className="text-[#8a93a3] mb-4">
                {search || sportFilter !== 'all'
                  ? sportFilter === 'non_playing' ? 'No members with Non-Playing category' : 'Try adjusting your filters' 
                  : 'Upload players via the Tournament Setup wizard'}
              </p>
              {!search && sportFilter === 'all' && (
                <Button 
                  onClick={() => window.location.href = '/admin/tournament-setup'}
                  className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
                >
                  Go to Tournament Setup
                </Button>
              )}
            </div>
          )}
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </Card>

      {/* Pagination */}
      {(hasMore || currentPage > 1) && !loading && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={currentPage <= 1}
            onClick={() => { const p = currentPage - 1; setCurrentPage(p); fetchPlayers(search, PAGE_SIZE * p); }}
            className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]">
            ← Prev
          </Button>
          <span className="text-sm text-[#8a93a3] px-3">Page {currentPage}</span>
          <Button variant="outline" size="sm" disabled={!hasMore}
            onClick={() => { const p = currentPage + 1; setCurrentPage(p); fetchPlayers(search, PAGE_SIZE * p); }}
            className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]"
            data-testid="players-next-page-button">
            Next →
          </Button>
        </div>
      )}

      {/* Add Member Dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) reset(); }}>
        <DialogContent className="bg-[#161a26] border-[#252a37]">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7]">Add New Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(handleCreatePlayer)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#8a93a3]">First Name *</Label>
                <Input {...register('first_name')} placeholder="John" className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1" />
                {errors.first_name && <p className="text-xs text-[#ef4444] mt-1">{errors.first_name.message}</p>}
              </div>
              <div>
                <Label className="text-[#8a93a3]">Last Name</Label>
                <Input {...register('last_name')} placeholder="Doe" className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-[#8a93a3]">Category *</Label>
              <Controller name="category" control={control} render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#161a26] border-[#252a37]">
                    {playerCategories.length > 0
                      ? playerCategories.map(cat => (
                          <SelectItem key={cat.code} value={cat.code} className="text-[#f0f2f7] focus:bg-[#1d2230]">
                            {cat.is_platinum ? '★ ' : ''}{cat.name}
                          </SelectItem>
                        ))
                      : <>
                          <SelectItem value="champion" className="text-[#f0f2f7] focus:bg-[#1d2230]">★ Champion</SelectItem>
                          <SelectItem value="advanced" className="text-[#f0f2f7] focus:bg-[#1d2230]">Advanced</SelectItem>
                        </>
                    }
                  </SelectContent>
                </Select>
              )} />
            </div>
            <div>
              <Label className="text-[#8a93a3]">Gender</Label>
              <Controller name="gender" control={control} render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#161a26] border-[#252a37]">
                    <SelectItem value="male" className="text-[#f0f2f7] focus:bg-[#1d2230]">Male</SelectItem>
                    <SelectItem value="female" className="text-[#f0f2f7] focus:bg-[#1d2230]">Female</SelectItem>
                  </SelectContent>
                </Select>
              )} />
            </div>
            <div>
              <Label className="text-[#8a93a3]">Phone</Label>
              <Input {...register('contact_phone')}
                placeholder="+91 98765 43210"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                onBlur={e => { const v = _normPhone(e.target.value); if (v !== e.target.value) { e.target.value = v; register('contact_phone').onChange({ target: e.target }); } }}
              />
              {errors.contact_phone && <p className="text-xs text-[#ef4444] mt-1">{errors.contact_phone.message}</p>}
            </div>
            <div>
              <Label className="text-[#8a93a3]">Email</Label>
              <Input {...register('contact_email')} placeholder="player@example.com" className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1" />
              {errors.contact_email && <p className="text-xs text-[#ef4444] mt-1">{errors.contact_email.message}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setCreateOpen(false); reset(); }} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
              <Button type="submit" disabled={creating} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
                {creating ? 'Creating...' : 'Add Member'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk CSV Upload Dialog */}
      <Dialog open={bulkOpen} onOpenChange={(open) => { setBulkOpen(open); if (!open) { setBulkFile(null); setBulkPreview([]); } }}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-lg" data-testid="players-bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#f5c518]" />
              Bulk Upload Members
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#1d2230] rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-[#f5c518] flex-shrink-0 mt-0.5" />
              <div className="text-xs text-[#8a93a3] space-y-1">
                <p><strong className="text-[#f0f2f7]">CSV columns:</strong> <code className="text-[#f5c518]">Name</code>, <code className="text-[#f5c518]">Last Name</code>, <code className="text-[#f5c518]">Mobile 1</code>, Mobile 2, Email, Date of Birth</p>
                <p>Name + Mobile 1 are required. Rows missing either are skipped.</p>
                <p className="text-[#ef4444]">Excel (.xlsx) not supported — save as CSV first.</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-[#8a93a3]">Choose CSV File</Label>
              <Button size="sm" variant="ghost" onClick={handleDownloadTemplate}
                className="text-xs text-[#f5c518] hover:text-[#f7d24a] h-7 px-2">
                <Download className="h-3 w-3 mr-1"/> Download Template
              </Button>
            </div>
              <input
                type="file"
                accept=".csv"
                onChange={handleBulkFileChange}
                className="block w-full text-sm text-[#8a93a3] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#1d2230] file:text-[#f5c518] file:font-medium hover:file:bg-[#252a37] cursor-pointer"
                data-testid="players-bulk-file-input"
              />
            {bulkPreview.length > 0 && (
              <div>
                <p className="text-xs text-[#8a93a3] mb-2">Preview (first 5 rows)</p>
                <div className="bg-[#1d2230] rounded-lg overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[#252a37]">
                        {Object.keys(bulkPreview[0]).slice(0, 5).map((h) => (
                          <th key={h} className="px-3 py-2 text-left text-[#8a93a3] font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {bulkPreview.map((row, i) => (
                        <tr key={`bulk-preview-${i}`} className="border-b border-[#252a37]/50">
                          {Object.values(row).slice(0, 5).map((val, j) => (
                            <td key={j} className="px-3 py-2 text-[#f0f2f7] truncate max-w-[100px]">{val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setBulkOpen(false); setBulkFile(null); setBulkPreview([]); }}
              className="border-[#252a37] text-[#f0f2f7]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkUpload}
              disabled={!bulkFile || bulkUploading}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="players-bulk-upload-submit"
            >
              {bulkUploading ? 'Uploading...' : 'Upload Members'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update DOB Dialog */}
      <Dialog open={dobOpen} onOpenChange={o => { setDobOpen(o); if (!o) { setDobFile(null); setDobPreview([]); } }}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-lg" data-testid="dob-update-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#f5c518]"/>
              Update Date of Birth
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#1d2230] rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-[#f5c518] flex-shrink-0 mt-0.5"/>
              <div className="text-xs text-[#8a93a3]">
                <p><strong className="text-[#f0f2f7]">CSV format:</strong> Two columns — <code className="text-[#f5c518]">Name</code> and <code className="text-[#f5c518]">Date of Birth</code></p>
                <p className="mt-1">No header row needed. Names are matched to existing members (fuzzy — minor spelling differences OK).</p>
                <p className="mt-1">DOB formats accepted: <code className="text-[#f5c518]">15/03/1990</code> · <code className="text-[#f5c518]">1990-03-15</code> · <code className="text-[#f5c518]">15-03-90</code></p>
              </div>
            </div>

            {/* Download template */}
            <div className="flex items-center justify-between">
              <Label className="text-[#8a93a3]">Choose CSV File</Label>
              <Button size="sm" variant="ghost"
                onClick={() => {
                  const csv = 'Rajan Sharma,15/03/1990\nPriya Patel,22/07/1995\nSuresh Kumar,10/11/1988';
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = 'DOB_Template.csv'; a.click();
                  URL.revokeObjectURL(url);
                }}
                className="text-xs text-[#f5c518] hover:text-[#f7d24a] h-7 px-2">
                <Download className="h-3 w-3 mr-1"/>Download Template
              </Button>
            </div>

            <input type="file" accept=".csv"
              onChange={handleDobFileChange}
              className="block w-full text-sm text-[#8a93a3] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#1d2230] file:text-[#f5c518] file:font-medium hover:file:bg-[#252a37] cursor-pointer"
              data-testid="dob-file-input"/>

            {dobPreview.length > 0 && (
              <div>
                <p className="text-xs text-[#8a93a3] mb-2">Preview ({dobPreview.length} rows shown)</p>
                <div className="bg-[#1d2230] rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[#252a37]">
                        <th className="px-3 py-2 text-left text-[#8a93a3]">Name</th>
                        <th className="px-3 py-2 text-left text-[#8a93a3]">DOB (raw)</th>
                        <th className="px-3 py-2 text-left text-[#8a93a3]">Parsed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dobPreview.map((row, i) => (
                        <tr key={`dob-preview-${i}`} className="border-b border-[#252a37]/50">
                          <td className="px-3 py-1.5 text-[#f0f2f7]">{row.name}</td>
                          <td className="px-3 py-1.5 text-[#8a93a3]">{row.raw_dob}</td>
                          <td className="px-3 py-1.5">
                            {row.dob
                              ? <span className="text-[#22c55e]">{row.dob}</span>
                              : <span className="text-[#ef4444]">Invalid format</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDobOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={handleDobUpload} disabled={!dobFile || dobUploading}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="dob-upload-submit">
              {dobUploading ? 'Updating...' : 'Update DOB'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
